import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────
// Button
// variant / size 視覺完全對齊 Figma 設計稿（從現有 .btn-brand 與
// 實際 className 提取的尺寸 token）。
// 用 Radix Slot 支持 asChild 模式（Link 包裝等）。
// ─────────────────────────────────────────────────────────────

const buttonVariants = cva(
  // ── 共通基底 ──
  'inline-flex items-center justify-center font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        /** 主色填充（對齊 .btn-brand） */
        brand:
          'rounded-md bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700',
        /** 主色外框（對齊 .btn-brand-outline） */
        'brand-outline':
          'rounded-md border border-brand-500 bg-white text-brand-600 hover:bg-brand-50 active:bg-brand-100',
        /** 淺色填充 */
        'brand-light':
          'rounded-md bg-brand-500/10 text-brand-500 hover:bg-brand-500/20',
        /** 灰色描邊（次要操作 / 取消） */
        outline:
          'rounded-md border border-gray-200 bg-white text-ink transition-colors hover:bg-gray-50 active:bg-gray-100',
        /** 幽灵按钮（无背景） */
        ghost:
          'rounded-md text-ink-soft hover:bg-gray-100 active:bg-gray-200',
        /** 纯链接样式 */
        link:
          'text-brand-500 underline-offset-4 hover:underline',
      },
      size: {
        /** h-8 text-sm px-4 — 表格行内、紧凑场景 */
        sm: 'h-8 px-4 text-sm',
        /** h-10 text-sm px-5 — 默认（对齐 .btn-brand） */
        md: 'h-10 px-5 text-sm',
        /** h-12 text-base px-6 — 大按钮（CTA / 确认） */
        lg: 'h-12 px-6 text-base font-semibold',
        /** 不带固定宽高（icon-only 或自定义尺寸） */
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'brand',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** true 时渲染子元素为根（如包裹 Link） */
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
